// The namespace, not named imports: a kit from before the receive pages (29 Oct) has none of these, and a named import
// would fail the editor's load instead of this one action.
import * as kitUi from '@flobi/asset-library/ui';
import apiClient, { artworkCmd } from '../services/apiClient';

/*
 * Send to Drive / Send with Handoff on a finished online export (cross-app pages, 29 Oct 2026). An export's files are
 * media assets already (artwork-back files each one as it is made), so nothing is uploaded: the kit's Send to list says
 * which apps take a file of the export's kind, and its sendMedia shapes each send (Drive asks for a folder, Handoff
 * opens its send screen, the Upscaler its editor or queue, Director a production's bin…). The export's links expire,
 * so its files are asked for afresh, as Download Again does.
 */

/** An app the export is sent on to: whichever the kit's Send to list offers (Drive, Handoff, Upscaler, Director…). */
export type SendApp = string;

interface ExportFile { name: string; url?: string; mediaAssetId?: string; contentType?: string; width?: number; height?: number }

type Tab = { go: (url: string) => void; close: () => void };
interface MediaItem { mediaAssetId?: string | null; name?: string; mime?: string | null; url?: string; thumbnailUrl?: string; width?: number; height?: number }
interface Kit {
  openTab?: () => Tab;
  mediaTargets?: (items: MediaItem[], options: { from: string }) => { app: string; name: string }[];
  sendMedia?: (app: string, what: { name: string; from: Record<string, unknown>; items: MediaItem[]; preview?: string }, options: { poster: Poster; open: () => Tab }) => Promise<string>;
}
type Poster = (getToken: unknown, path: string, data: unknown) => Promise<unknown>;

/** The kit's calls go through Artwork's own client: Artwork never points the kit at the gateway itself. */
const poster: Poster = (_getToken, path, data) => apiClient.post(path, data).then((response) => response?.data);

/** An export's files as parcel items: the ones kept as media assets, with what each is. */
export function exportItems(files: readonly ExportFile[]) {
  return files
    .filter((file) => !!file.mediaAssetId)
    .map((file) => ({
      mediaAssetId: file.mediaAssetId,
      name: file.name,
      mime: file.contentType || null,
      ...(file.url ? { url: file.url } : {}),
      ...(file.url && /^image\//.test(file.contentType || '') ? { thumbnailUrl: file.url } : {}),
      ...(file.width && file.height ? { width: file.width, height: file.height } : {}),
    }));
}

const FORMAT_MIME: Record<string, string> = { jpg: 'image/jpeg', png: 'image/png', pdf: 'application/pdf', mp4: 'video/mp4', gif: 'image/gif' };

/**
 * The buttons a finished online export of `format` gets: the kit's Send to list for a file of that kind (the export's
 * own files are only read when a button is pressed). Each is `{ app, label }`, the label just the app's name: the
 * panel heads them Open with, Drive first (30 Oct). Empty with a kit from before 29 Oct.
 */
export function exportTargets(format: string): { app: SendApp; label: string }[] {
  const kit = kitUi as unknown as Kit;
  const mime = FORMAT_MIME[format];
  if (!kit.mediaTargets || !mime) return [];
  // A stand-in for the file to come. Artwork itself is left out (it is the sender), so its one-picture rule never applies.
  const file: MediaItem = { mediaAssetId: 'export', mime, url: 'https://storage.flobi.ai/export' };
  return kit
    .mediaTargets([file], { from: 'artwork' })
    .map(({ app, name }) => ({ app, label: name }));
}

/** Sends export `jobId` (named `name`, the file's name) on to `app`; answers the address opened, throws a sentence. */
export async function sendExport(app: SendApp, jobId: string, name: string): Promise<string> {
  const kit = kitUi as unknown as Kit;
  if (!kit.openTab || !kit.sendMedia) throw new Error('Sending exports needs the latest Flobi kit. Update it, then try again.');
  // Opened at the press: a tab opened after the calls below is a popup the browser blocks.
  const tab = kit.openTab();
  try {
    const job = await artworkCmd<{ files: ExportFile[] }>('artwork.export.get', { jobId });
    const items = exportItems(job.files || []);
    if (items.length === 0) throw new Error('That export is no longer kept. Export it again.');
    return await kit.sendMedia(app, { name, from: { app: 'artwork', name }, items, preview: items.find((item) => item.thumbnailUrl)?.thumbnailUrl }, { poster, open: () => tab });
  } catch (error) {
    tab.close();
    throw error;
  }
}
